import { getAccessToken, notifyAuthFailure } from "../auth/auth-client";
import { Api } from "./api";
import { parseNdjsonStream } from "./parser";

import type {
  ClassificationLine,
  ClassificationProgressEvent,
  ClassificationRequest,
  ClassificationResponse,
  ClientConfig,
  ProfileSummary,
  StyleDefinition,
  StyleProfileResponse,
} from "../types";

type ClassifyDocumentArgs = {
  paragraphs: ClassificationLine[];
  profile?: string;
  title?: string;
  bisac?: string;
  lockExistingStyles?: boolean;
  /** Correlates this request with the run's Sentry report on both sides. */
  runId?: string;
  onUpdateProgress?: (e: ClassificationProgressEvent) => void;
};

export const serverApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    getAvailableProfiles: builder.query<ProfileSummary[], void>({
      query: () => {
        return {
          url: "style-profiles/",
        };
      },
      providesTags: ["style-profiles"],
    }),
    getStyleProfile: builder.query<StyleProfileResponse, string>({
      query: (profileName) => {
        return {
          url: `style-profiles/${encodeURIComponent(profileName)}`,
        };
      },
    }),
    getFlattenedStyles: builder.query<StyleDefinition[], string>({
      query: (profileName) => {
        return {
          url: `style-profiles/${encodeURIComponent(profileName)}/styles`,
        };
      },
    }),
    getClientConfig: builder.query<ClientConfig, void>({
      query: () => {
        return {
          url: "config/client",
        };
      },
    }),
    getVersion: builder.query<{ version: string }, void>({
      query: () => {
        return {
          url: "version",
        };
      },
    }),
    classifyDocument: builder.mutation<ClassificationResponse, ClassifyDocumentArgs>({
      async queryFn({
        profile,
        paragraphs,
        title,
        bisac,
        lockExistingStyles,
        runId,
        onUpdateProgress,
      }) {
        if (!paragraphs.length) return { data: { lines: [] } };

        const params = new URLSearchParams();
        if (profile) {
          params.set("style_profile_name", profile);
        }
        if (lockExistingStyles) {
          params.set("lock_existing_styles", "true");
        }

        const base = `${__MSS_API_BASE_URL__}/api/styling/classify`;
        const queryString = params.toString();
        const url = queryString ? `${base}?${queryString}` : base;

        const body: ClassificationRequest = { paragraphs, title, bisac };

        try {
          const headers: Record<string, string> = { "Content-Type": "application/json" };
          if (runId) {
            headers["X-MSS-Run-Id"] = runId;
          }
          if (!__MSS_AUTH_DISABLED__) {
            const token = await getAccessToken();
            if (token) {
              headers["Authorization"] = `Bearer ${token}`;
            }
          }

          const response = await fetch(url, {
            method: "POST",
            headers,
            body: JSON.stringify(body),
          });

          if (!response.ok) {
            if (response.status === 401) {
              notifyAuthFailure();
            }
            const errorText = await response.text().catch((e) => e.message);
            return {
              error: {
                status: response.status,
                data: `${response.statusText}\n${errorText}`,
              },
            };
          }

          const result = await parseNdjsonStream(response, onUpdateProgress);
          return { data: result };
        } catch (err) {
          console.error("classifyDocument: ", err);
          return {
            error: {
              status: "FETCH_ERROR" as const,
              error: err instanceof Error ? err.message : String(err),
            },
          };
        }
      },
    }),
  }),
});

export const {
  useClassifyDocumentMutation,
  useGetAvailableProfilesQuery,
  useGetClientConfigQuery,
  useGetFlattenedStylesQuery,
  useGetStyleProfileQuery,
  useGetVersionQuery,
  useLazyGetFlattenedStylesQuery,
  useLazyGetStyleProfileQuery,
} = serverApi;
